import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/AuthContext';
import LeagueSettings from '../components/LeagueSettings';

// Inline utility functions to avoid import issues
const DEFAULT_LOCK_BUFFER_MINUTES = 30;

function getCurrentGMTTime(): Date {
  return new Date();
}

function parseMatchDateToGMT(dateString: string): Date | null {
  if (!dateString) {
    return null;
  }

  try {
    if (dateString.includes('T') && dateString.includes('Z')) {
      return new Date(dateString);
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  } catch (error) {
    console.error('Error parsing match date:', error);
    return null;
  }
}

function isMatchLocked(match: any, lockBufferMinutes: number = DEFAULT_LOCK_BUFFER_MINUTES): boolean {
  if (!match || !match.match_date) {
    return false;
  }
  
  const matchDateGMT = parseMatchDateToGMT(match.match_date);
  if (!matchDateGMT) {
    return false;
  }

  const nowGMT = getCurrentGMTTime();
  const lockTime = new Date(matchDateGMT.getTime() - (lockBufferMinutes * 60 * 1000));
  
  return nowGMT >= lockTime;
}

function getMatchStatus(match: any): string {
  if (match.status === 'live') {
    return 'Live';
  }
  
  if (match.status === 'completed') {
    return 'Completed';
  }
  
  if (match.match_date) {
    const matchDateGMT = parseMatchDateToGMT(match.match_date);
    if (matchDateGMT) {
      const nowGMT = getCurrentGMTTime();
      if (nowGMT >= matchDateGMT) {
        return 'Started';
      }
    }
  }
  
  return 'Upcoming';
}

function getMatchStatusClasses(status: string): string {
  switch (status) {
    case 'Completed':
      return 'bg-green-100 text-green-800';
    case 'Live':
      return 'bg-red-100 text-red-800';
    case 'Started':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-blue-100 text-blue-800';
  }
}

interface Match {
  id: string;
  name: string;
  team1_name: string;
  team2_name: string;
  team1_shortname?: string;
  team2_shortname?: string;
  match_date: string;
  status: string;
  winner?: string;
  squad_players?: any[];
}

interface Prediction {
  id: number;
  user_id?: number;
  match_id: string;
  predicted_winner: string;
  is_correct?: boolean;
  points_awarded?: number;
  updated_at: string;
  display_name?: string;
  mom_prediction?: string;
  actual_winner?: string;
}

interface Participant {
  user_id: number;
  display_name: string;
  email: string;
  is_manager: boolean;
  elimination_status: 'active' | 'eliminated';
  wrong_picks_count: number;
  joined_at: string;
}

interface Survivor {
  user_id: number;
  display_name: string;
  total_predictions: number;
  correct_predictions: number;
  wrong_predictions: number;
  joined_at: string;
  is_manager: boolean;
  status: 'active' | 'eliminated';
}

interface League {
  id: number;
  name: string;
  tournament_name: string;
  format: string;
  year: number;
  start_date: string;
  end_date: string;
  current_participants: number;
  max_participants?: number;
  created_by: number;
  created_at: string;
  join_code: string;
  is_public: boolean;
  passcode?: string;
}

interface LeagueSettings {
  wrong_picks_allowed: string;
}

type EliminatorMainTab = 'info' | 'mypicks' | 'activity' | 'survivors';

function eliminatorTabFromSearchParam(raw: string | null): EliminatorMainTab | null {
  if (raw === 'standings') return 'survivors';
  if (raw === 'info' || raw === 'mypicks' || raw === 'activity' || raw === 'survivors') return raw;
  return null;
}

const EliminatorLeaguePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  console.log('🔍 EliminatorLeaguePage: leagueId from URL:', id);
  console.log('🔍 EliminatorLeaguePage: user:', user);
  
  const [league, setLeague] = useState<League | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [survivors, setSurvivors] = useState<Survivor[]>([]);
  const [allPredictions, setAllPredictions] = useState<Prediction[]>([]);
  const [matchSquadPlayers, setMatchSquadPlayers] = useState<{[key: string]: any[]}>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<EliminatorMainTab>(() => {
    return eliminatorTabFromSearchParam(searchParams.get('tab')) ?? 'mypicks';
  });
  const [showSettings, setShowSettings] = useState(false);
  const [viewMode, setViewMode] = useState<'dropdown' | 'list'>('dropdown');
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  
  // Batch prediction state (similar to pickem)
  const [batchPredictions, setBatchPredictions] = useState<{[key: string]: string}>({});
  const [savingPredictions, setSavingPredictions] = useState(false);
  const [eliminationStatus, setEliminationStatus] = useState<{wrong_picks: number, wrong_picks_allowed: number, is_eliminated: boolean} | null>(null);
  const [leagueSettings, setLeagueSettings] = useState<LeagueSettings>({ wrong_picks_allowed: '0' });
  const [leagueHasStarted, setLeagueHasStarted] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedLeagueName, setEditedLeagueName] = useState('');
  const [isEditingMaxParticipants, setIsEditingMaxParticipants] = useState(false);
  const [editedMaxParticipants, setEditedMaxParticipants] = useState<number>(0);
  const [isUpdatingMaxParticipants, setIsUpdatingMaxParticipants] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const isManager = league?.created_by === user?.id;

  // 🏆 Use centralized match lock management with eliminator-specific status
  const getMatchStatusLocal = (match: Match) => {
    // For eliminator leagues, we need to distinguish between:
    // - "Started": Match has passed start time but no result is set
    // - "Completed": Admin has set the match result (winner is determined)
    // - "Abandoned": Match was abandoned
    
    // Check match.status first (this is the source of truth from database)
    if (match.status === 'abandoned') {
      return 'Abandoned';
    }
    
    if (match.status === 'completed') {
      return 'Completed';
    }
    
    if (match.status === 'live') {
      return 'Live';
    }
    
    // Fallback: Check if match has a winner (completed match with result)
    // This is for backward compatibility with old data
    // Exclude "N/A" which is used for abandoned matches
    if (match.winner && match.winner.trim() !== '' && match.winner.trim() !== 'N/A') {
      return 'Completed';
    }
    
    // Check if match has started (passed start time) - using GMT/UTC
    if (match.match_date) {
      const matchDateGMT = parseMatchDateToGMT(match.match_date);
      if (matchDateGMT) {
        const nowGMT = getCurrentGMTTime();
        
        if (nowGMT >= matchDateGMT) {
          return 'Started';
        }
      }
    }
    
    return 'Upcoming';
  };

  const isMatchLockedLocal = (match: Match) => {
    return isMatchLocked(match, DEFAULT_LOCK_BUFFER_MINUTES);
  };

  // 🏆 Get prediction for a specific match
  const getPredictionForMatch = (matchId: string) => {
    if (!Array.isArray(predictions)) return null;
    return predictions.find(p => p.match_id === matchId);
  };

  // 🏆 Get next available match for prediction
  const getNextAvailableMatch = () => {
    if (!Array.isArray(matches)) return null;
    
    const nowGMT = getCurrentGMTTime();
    const upcomingMatches = matches.filter(match => {
      const matchDateGMT = parseMatchDateToGMT(match.match_date);
      return matchDateGMT ? matchDateGMT > nowGMT && !isMatchLockedLocal(match) : false;
    });
    
    if (upcomingMatches.length === 0) return null;
    
    // Sort by date and return the earliest
    return upcomingMatches.sort((a, b) => {
      const dateA = parseMatchDateToGMT(a.match_date);
      const dateB = parseMatchDateToGMT(b.match_date);
      return dateA && dateB ? dateA.getTime() - dateB.getTime() : 0;
    })[0];
  };

  // 🏆 Get short match name for grid display
  const getShortMatchName = (match: Match) => {
    // Use official short names from CricAPI if available
    if (match.team1_shortname && match.team2_shortname) {
      return `${match.team1_shortname} vs ${match.team2_shortname}`;
    }
    
    // Fallback: Clean up team names by removing any match descriptions
    const cleanTeamName = (teamName: string) => {
      // Remove anything after a comma (like ", 1st Match")
      const cleaned = teamName.split(',')[0].trim();
      // Get the last part of the team name (usually the main identifier)
      return cleaned.includes(' ') ? cleaned.split(' ').slice(-1)[0] : cleaned;
    };
    
    // If we have team names, clean them up and use them
    if (match.team1_name && match.team2_name) {
      const team1Short = cleanTeamName(match.team1_name);
      const team2Short = cleanTeamName(match.team2_name);
      
      return `${team1Short} vs ${team2Short}`;
    }
    
    // Primary: try to extract team names from match name (e.g., "Zimbabwe vs South Africa, 1st Match")
    if (match.name) {
      // Pattern for "Team1 vs Team2, Description"
      const vsPattern = /^(.+?)\s+vs\s+(.+?)(?:\s*,\s*.+)?$/i;
      const nameMatch = match.name.match(vsPattern);
      
      if (nameMatch) {
        const team1 = nameMatch[1].trim();
        const team2 = nameMatch[2].trim();
        
        // Get short names (last word of each team name)
        const team1Short = team1.includes(' ') ? team1.split(' ').slice(-1)[0] : team1;
        const team2Short = team2.includes(' ') ? team2.split(' ').slice(-1)[0] : team2;
        
        return `${team1Short} vs ${team2Short}`;
      }
    }
    
    // Final fallback: use match name or generic
    return match.name || `Match ${match.id}`;
  };

  // Get short name for a predicted winner
  const getPredictedWinnerShortName = (predictedWinner: string, match: Match) => {
    // If the predicted winner matches team1_name, return team1_shortname
    if (predictedWinner === match.team1_name && match.team1_shortname) {
      return match.team1_shortname;
    }
    
    // If the predicted winner matches team2_name, return team2_shortname
    if (predictedWinner === match.team2_name && match.team2_shortname) {
      return match.team2_shortname;
    }
    
    // Fallback: return the predicted winner as is (should be clean team name now)
    return predictedWinner;
  };

  // 🏆 Check if league has started
  const checkIfLeagueHasStarted = () => {
    if (!league || !matches.length) return false;
    const leagueCreatedAt = new Date(league.created_at || new Date());
    const matchesAfterLeagueCreation = matches.filter(match => {
      const matchDate = new Date(match.match_date);
      return matchDate >= leagueCreatedAt;
    }).sort((a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime());
    
    if (matchesAfterLeagueCreation.length === 0) return false;
    const firstMatchAfterCreation = matchesAfterLeagueCreation[0];
    
    // If the first match is already live/completed, the league has started
    if (firstMatchAfterCreation.status === 'live' || firstMatchAfterCreation.status === 'completed') {
      return true;
    }

    // Compare using GMT to avoid timezone shifting the start too early
    const matchDateGMT = parseMatchDateToGMT(firstMatchAfterCreation.match_date);
    const nowGMT = getCurrentGMTTime();
    return !!(matchDateGMT && nowGMT && nowGMT >= matchDateGMT);
  };

  // 🏆 Load league data
  const loadLeagueData = async () => {
    console.log('🚀 loadLeagueData function STARTED');
    if (!id) {
      console.log('❌ No league ID provided');
      return;
    }
    
    console.log('🔍 Loading eliminator league data for ID:', id);
    setLoading(true);
    setError(null);

    try {
      if (!id) {
        setError('League ID is required');
        setLoading(false);
        return;
      }

      const leagueId = parseInt(id);
      if (isNaN(leagueId)) {
        setError('Invalid league ID');
        setLoading(false);
        return;
      }

      // Load league details
      console.log('🔍 Loading league details...');
      try {
        const leagueData = await apiService.getEliminatorLeague(leagueId);
        console.log('🔍 League data:', leagueData);
        console.log('🔍 League data.league:', leagueData.league);
        console.log('🔍 League data.league type:', typeof leagueData.league);
          setLeague(leagueData.league);
          setEditedLeagueName(leagueData.league.name);
          if (leagueData.league.max_participants) {
            setEditedMaxParticipants(leagueData.league.max_participants);
          }
      } catch (err: any) {
        console.error('❌ Failed to load league details:', err);
        throw err;
      }

      // Load matches - ALWAYS load matches
      console.log('🎯 ABOUT TO LOAD MATCHES - This should appear!');
      console.log('🔍 Loading matches...');
      try {
        console.log('🔍 Calling getEliminatorMatches with leagueId:', leagueId);
        const matchesData = await apiService.getEliminatorMatches(leagueId);
        console.log('🔍 Matches API response:', matchesData);
        console.log('🔍 Matches data.success:', matchesData.success);
        console.log('🔍 Matches data.matches:', matchesData.matches);
        console.log('🔍 Matches data.matches type:', typeof matchesData.matches);
        console.log('🔍 Matches data.matches length:', Array.isArray(matchesData.matches) ? matchesData.matches.length : 'not array');
        
        if (matchesData.success && Array.isArray(matchesData.matches)) {
          setMatches(matchesData.matches);
          console.log('✅ Matches set successfully:', matchesData.matches.length);
        } else {
          console.warn('⚠️ No matches found or invalid response');
          setMatches([]);
        }
      } catch (err: any) {
        console.error('❌ Failed to load matches:', err);
        console.error('❌ Matches error details:', err.response?.data || err.message);
        setMatches([]); // Set empty array instead of throwing
      }

      // Test: Try to load matches again with a simple approach
      console.log('🔍 Testing matches loading again...');
      try {
        const testMatchesData = await apiService.getEliminatorMatches(leagueId);
        console.log('🔍 Test matches response:', testMatchesData);
      } catch (err: any) {
        console.error('❌ Test matches failed:', err);
      }

      // Load predictions
      console.log('🔍 Loading predictions...');
      try {
        const predictionsData = await apiService.getEliminatorPredictions(leagueId);
        console.log('🔍 Predictions data:', predictionsData);
        setPredictions(predictionsData.predictions);
      } catch (err: any) {
        console.error('❌ Failed to load predictions:', err);
        throw err;
      }

      // Enrich matches with winner data
      const updatedMatches = matches.map((match: Match) => {
        const predictionWithWinner = predictions.find((p: any) =>
          p.match_id === match.id && (p as any).winner
        );
        if (predictionWithWinner) {
          return { ...match, winner: (predictionWithWinner as any).winner };
        }
        return match;
      });
      setMatches(updatedMatches);

      // Load participants
      console.log('🔍 Loading participants...');
      try {
        const participantsData = await apiService.getEliminatorLeagueParticipants(leagueId);
        console.log('🔍 Participants data:', participantsData);
        console.log('🔍 Participants data.participants:', participantsData.participants);
        console.log('🔍 Participants data.participants type:', typeof participantsData.participants);
        setParticipants(participantsData.participants);
      } catch (err: any) {
        console.error('❌ Failed to load participants:', err);
        throw err;
      }

      // Load survivors
      console.log('🔍 Loading survivors...');
      try {
        const survivorsData = await apiService.getEliminatorSurvivors(leagueId);
        console.log('🔍 Survivors data:', survivorsData);
        setSurvivors(survivorsData.survivors);
      } catch (err: any) {
        console.error('❌ Failed to load survivors:', err);
        throw err;
      }

      // Load all predictions for survivors grid
      console.log('🔍 Loading all predictions...');
      try {
        const allPredictionsData = await apiService.getAllEliminatorPredictions(leagueId);
        console.log('🔍 All predictions data:', allPredictionsData);
        setAllPredictions(allPredictionsData.predictions);
      } catch (err: any) {
        console.error('❌ Failed to load all predictions:', err);
        throw err;
      }

      // Load elimination status
      console.log('🔍 Loading elimination status...');
      try {
        const eliminationData = await apiService.checkEliminationStatus(leagueId);
        console.log('🔍 Elimination data:', eliminationData);
        setEliminationStatus(eliminationData.status);
      } catch (err: any) {
        console.error('❌ Failed to load elimination status:', err);
        throw err;
      }

      // Load league settings
      console.log('🔍 Loading league settings...');
      try {
        const settingsData = await apiService.getEliminatorLeagueSettings(leagueId);
        console.log('🔍 Settings data:', settingsData);
        setLeagueSettings(settingsData.settings);
        
        // Debug wrong picks calculation
        console.log('🔍 League Settings Debug:', settingsData.settings);
        console.log('🔍 Wrong picks allowed:', settingsData.settings?.wrong_picks_allowed);
        console.log('🔍 Survivors Debug:', survivors);
        survivors.forEach((survivor: any, index: number) => {
          console.log(`🔍 Survivor ${index + 1}:`, {
            name: survivor.display_name,
            wrong_predictions: survivor.wrong_predictions,
            status: survivor.status,
            wrong_picks_left: parseInt(settingsData.settings?.wrong_picks_allowed || '3') - survivor.wrong_predictions
          });
        });
      } catch (err: any) {
        console.error('❌ Failed to load league settings:', err);
        throw err;
      }

      // Pre-load squad data for all matches that have squad_players
      const squadData: {[key: string]: any[]} = {};
      updatedMatches.forEach((match: Match) => {
        if (match.squad_players && match.squad_players.length > 0) {
          squadData[match.id] = match.squad_players;
        }
      });
      setMatchSquadPlayers(squadData);

      console.log('🔍 All eliminator league data loaded successfully!');

      // Force load matches if they're still empty
      if (matches.length === 0) {
        console.log('🔄 Forcing matches reload because matches array is empty');
        try {
          const forceMatchesData = await apiService.getEliminatorMatches(leagueId);
          console.log('🔄 Force matches response:', forceMatchesData);
          if (forceMatchesData.success && Array.isArray(forceMatchesData.matches)) {
            setMatches(forceMatchesData.matches);
            console.log('✅ Force matches set successfully:', forceMatchesData.matches.length);
          }
        } catch (err: any) {
          console.error('❌ Force matches failed:', err);
        }
      }

    } catch (err: any) {
      console.error('❌ Error loading eliminator league data:', err);
      console.error('❌ Error details:', err.response?.data || err.message);
      setError(err.message || 'Failed to load league data');
    } finally {
      setLoading(false);
    }
  };

  // 🏆 Make prediction
  const makePrediction = async (matchId: string, predictedWinner: string) => {
    try {
      const response = await apiService.makeEliminatorPrediction(parseInt(id!), matchId, predictedWinner);
      if (response.success) {
        setNotification('Prediction saved successfully!');
        setTimeout(() => setNotification(null), 3000);
        loadLeagueData(); // Reload data to update predictions
      } else {
        setError(response.message || 'Failed to save prediction');
      }
    } catch (err: any) {
      console.error('Error making prediction:', err);
      setError(err.message || 'Failed to save prediction');
    }
  };

  // Batch prediction functions (similar to pickem)
  const handleBatchPrediction = (matchId: string, predictedWinner: string) => {
    setBatchPredictions(prev => ({
      ...prev,
      [matchId]: predictedWinner
    }));
  };

  const getCurrentPrediction = (matchId: string) => {
    // Prioritize batch predictions (current selection) over saved predictions
    const current = batchPredictions[matchId] || '';
    return current;
  };

  const getSavedPrediction = (matchId: string) => {
    // Get the saved prediction from the database
    const existingPrediction = predictions.find(p => p.match_id === matchId);
    return existingPrediction?.predicted_winner || '';
  };

  const handleSaveBatchPredictions = async () => {
    const hasPredictions = Object.keys(batchPredictions).length > 0;
    
    if (!hasPredictions) {
      setNotification('No predictions to save');
      return;
    }

    setSavingPredictions(true);
    try {
      const leagueId = parseInt(id!);
      const promises = Object.entries(batchPredictions).map(([matchId, predictedWinner]) => {
        return apiService.makeEliminatorPrediction(leagueId, matchId, predictedWinner);
      });
      
      await Promise.all(promises);
      console.log('🔍 All eliminator predictions saved successfully');
      
      // Reload predictions
      const predictionsData = await apiService.getEliminatorPredictions(leagueId);
      setPredictions(predictionsData.predictions);
      
      // Update batch state with confirmed saved data
      const updatedBatchPredictions = { ...batchPredictions };
      predictionsData.predictions.forEach((prediction: any) => {
        if (prediction.predicted_winner) {
          updatedBatchPredictions[prediction.match_id] = prediction.predicted_winner;
        }
      });
      
      setBatchPredictions(updatedBatchPredictions);
      setNotification('All predictions saved successfully!');
      setTimeout(() => setNotification(null), 3000);
    } catch (err: any) {
      console.error('🔍 Error saving eliminator predictions:', err);
      setError(err.message || 'Failed to save predictions');
    } finally {
      setSavingPredictions(false);
    }
  };

  // 🏆 Handle league deletion
  const handleLeagueDeleted = () => {
    navigate('/tournaments');
  };

  // 🏆 Handle leaving league
  // Handle rename league
  const handleRenameLeague = async (newName: string) => {
    if (!league || !newName.trim()) return;
    
    setIsRenaming(true);
    try {
      const result = await apiService.updateEliminatorLeagueName(league.id, newName);
      if (result.success) {
        setLeague({ ...league, name: newName });
        setEditedLeagueName(newName);
        setIsEditingName(false);
      } else {
        alert(result.message || 'Failed to update league name');
        setEditedLeagueName(league.name);
      }
    } catch (error: any) {
      console.error('Error renaming league:', error);
      alert(error.message || 'Failed to update league name');
      setEditedLeagueName(league.name);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleUpdateMaxParticipants = async (newMax: number) => {
    if (!league || newMax === league.max_participants || newMax < league.current_participants) {
      return;
    }

    setIsUpdatingMaxParticipants(true);
    try {
      const result = await apiService.updateFantasyLeagueMaxParticipants(league.id, newMax);
      if (result.success) {
        setLeague({ ...league, max_participants: newMax });
        setEditedMaxParticipants(newMax);
        setIsEditingMaxParticipants(false);
        setNotification('Max participants updated successfully!');
        setTimeout(() => setNotification(null), 3000);
      } else {
        setNotification(result.message || 'Failed to update max participants');
        setTimeout(() => setNotification(null), 3000);
        setEditedMaxParticipants(league.max_participants || 0);
      }
    } catch (err: any) {
      console.error('Error updating max participants:', err);
      setNotification(err.message || 'Failed to update max participants');
      setTimeout(() => setNotification(null), 3000);
      setEditedMaxParticipants(league.max_participants || 0);
    } finally {
      setIsUpdatingMaxParticipants(false);
    }
  };

  const handleSaveEliminatorSettings = async () => {
    if (!league || !isManager || leagueHasStarted) return;
    const rawValue = leagueSettings.wrong_picks_allowed || '0';
    const parsed = parseInt(rawValue, 10);

    if (isNaN(parsed) || parsed <= 0) {
      setNotification('Enter a number greater than 0 for wrong picks allowed.');
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    setSavingSettings(true);
    try {
      const payload = { wrong_picks_allowed: parsed.toString() };
      const result = await apiService.updateEliminatorLeagueSettings(league.id, payload);

      if (result.success || result.result) {
        setLeagueSettings(payload);
        setNotification('Eliminator settings updated.');
        setTimeout(() => setNotification(null), 3000);
      } else {
        setNotification(result.message || 'Failed to update settings.');
        setTimeout(() => setNotification(null), 3000);
      }
    } catch (err: any) {
      console.error('Error updating eliminator settings:', err);
      setNotification(err.message || 'Failed to update settings.');
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleLeaveLeague = async () => {
    if (!id) return;
    
    try {
      // TODO: Implement leave league functionality
      alert('Leave league functionality to be implemented');
    } catch (err: any) {
      console.error('Error leaving league:', err);
      alert(err.message || 'Failed to leave league');
    }
  };

  // 🏆 Load data on component mount
  useEffect(() => {
    console.log('🔍 useEffect triggered with leagueId:', id);
    console.log('🚀 ABOUT TO CALL loadLeagueData');
    if (id) {
      console.log('�� Calling loadLeagueData...');
      loadLeagueData();
    } else {
      console.log('❌ leagueId is undefined, not calling loadLeagueData');
    }
  }, [id]);

  // 🏆 Check if league has started
  useEffect(() => {
    const hasStarted = checkIfLeagueHasStarted();
    setLeagueHasStarted(hasStarted);
    console.log('🔍 League has started:', hasStarted);
  }, [league, matches]);

  useEffect(() => {
    const t = eliminatorTabFromSearchParam(searchParams.get('tab'));
    if (t != null && t !== activeTab) {
      setActiveTab(t);
    }
  }, [searchParams, activeTab]);

  // 🏆 Auto-select next available match in dropdown view
  useEffect(() => {
    if (activeTab === 'mypicks' && viewMode === 'dropdown' && !selectedMatchId) {
      const nextMatch = getNextAvailableMatch();
      if (nextMatch) {
        setSelectedMatchId(nextMatch.id);
      }
    }
  }, [activeTab, viewMode, selectedMatchId, matches]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7] flex items-center justify-center">
        <div className="text-white text-xl">Loading Eliminator League...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7] flex items-center justify-center">
        <div className="text-red-400 text-xl">Error: {error}</div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7] flex items-center justify-center">
        <div className="text-white text-xl">League not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7]">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-2 sm:p-4 md:p-5">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate('/my-leagues')}
                className="text-blue-100 hover:text-white transition-colors duration-200 mb-1 sm:mb-2 flex items-center text-xs sm:text-sm"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Back to Leagues</span>
                <span className="sm:hidden">Back</span>
              </button>
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-accent mb-1 truncate">{league.name}</h1>
              <p className="text-blue-100 text-xs sm:text-sm md:text-base">
                <span className="truncate block sm:inline">{league.tournament_name}</span>
                <span className="hidden sm:inline"> • </span>
                <span className="block sm:inline">{league.format} • {league.year}</span>
              </p>
            </div>
            <div className="text-left sm:text-right flex-shrink-0">
              <div className="inline-flex items-center space-x-1 sm:space-x-2">
                <span className="text-blue-100 text-xs sm:text-sm">Survivors:</span>
                <span className="text-white text-xs sm:text-sm md:text-base font-semibold">{league.current_participants}</span>
                {eliminationStatus && (
                  <span className={`text-xs sm:text-sm ml-2 ${eliminationStatus.is_eliminated ? 'text-red-300' : 'text-green-300'}`}>
                    {eliminationStatus.is_eliminated ? '❌ Eliminated' : '✅ Active'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-3 sm:py-4 md:py-5">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-1 border border-white/20 mb-6 sm:mb-8 overflow-x-auto">
          <div className="flex space-x-1">
            {[
              { id: 'info', label: 'League Info', icon: 'ℹ️', shortLabel: 'Info' },
              { id: 'mypicks', label: 'My Picks', icon: '🎯', shortLabel: 'Picks' },
              { id: 'survivors', label: 'Survivors', icon: '🏆', shortLabel: 'Survivors' },
              { id: 'activity', label: 'Activity', icon: '📊', shortLabel: 'Activity' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  const tid = tab.id as EliminatorMainTab;
                  setActiveTab(tid);
                  setSearchParams({ tab: tid });
                }}
                className={`flex-1 py-2 sm:py-2.5 px-2 sm:px-3 md:px-4 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-accent text-primary shadow-lg transform scale-105'
                    : 'text-white hover:text-accent hover:bg-white/10'
                }`}
              >
                <span className="mr-1 sm:mr-2">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* League Info Tab */}
        {activeTab === 'info' && (
          <>
          <div className="grid md:grid-cols-2 gap-8">
            {/* League Details */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <span className="mr-3">🏆</span>
                  League Details
                </h2>
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  title="Toggle Settings"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-blue-200">League Name:</span>
                  <div className="flex items-center space-x-2">
                    {isEditingName ? (
                      <>
                        <input
                          type="text"
                          value={editedLeagueName}
                          onChange={(e) => setEditedLeagueName(e.target.value)}
                          className="px-2 py-1 bg-white/20 border border-white/30 rounded text-white font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
                          autoFocus
                        />
                        <button
                          onClick={async () => {
                            if (editedLeagueName.trim() && editedLeagueName.trim() !== league.name) {
                              await handleRenameLeague(editedLeagueName.trim());
                            }
                            setIsEditingName(false);
                          }}
                          disabled={isRenaming || !editedLeagueName.trim() || editedLeagueName.trim() === league.name}
                          className="text-green-400 hover:text-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Save"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingName(false);
                            setEditedLeagueName(league.name);
                          }}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          title="Cancel"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-white font-semibold">{league.name}</span>
                        {isManager && (
                          <button
                            onClick={() => {
                              setEditedLeagueName(league.name);
                              setIsEditingName(true);
                            }}
                            className="text-blue-300 hover:text-blue-200 transition-colors ml-2"
                            title="Edit league name"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-blue-200">Tournament:</span>
                  <span className="text-white font-semibold">{league.tournament_name}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-blue-200">Format:</span>
                  <span className="text-white font-semibold">{league.format}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-blue-200">Year:</span>
                  <span className="text-white font-semibold">{league.year}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-blue-200">Status:</span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                    Active
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-blue-200">Type:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    league.is_public 
                      ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                      : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                  }`}>
                    {league.is_public ? 'Public' : 'Private'}
                  </span>
                </div>
                {league.join_code && (
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-blue-200">Join Code:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-mono bg-white/10 px-3 py-1 rounded border border-white/20">
                        {league.join_code}
                      </span>
                      <button
                        onClick={() => navigator.clipboard.writeText(league.join_code!)}
                        className="text-blue-300 hover:text-blue-200 transition-colors duration-200"
                        title="Copy join code"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                {league.passcode && (
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-blue-200">League Passcode:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-mono bg-white/10 px-3 py-1 rounded border border-white/20">
                        {league.passcode}
                      </span>
                      <button
                        onClick={() => navigator.clipboard.writeText(league.passcode!)}
                        className="text-blue-300 hover:text-blue-200 transition-colors duration-200"
                        title="Copy passcode"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                {league.created_at && (
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-blue-200">Created:</span>
                    <span className="text-white font-medium">{new Date(league.created_at).toLocaleDateString()}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-blue-200">Participants:</span>
                  <div className="flex items-center space-x-2">
                    {isEditingMaxParticipants ? (
                      <>
                        <span className="text-white font-semibold">{league.current_participants}/</span>
                        <input
                          type="number"
                          value={editedMaxParticipants}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val >= league.current_participants) {
                              setEditedMaxParticipants(val);
                            }
                          }}
                          min={league.current_participants}
                          className="w-20 px-2 py-1 bg-white/20 border border-white/30 rounded text-white font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
                          autoFocus
                        />
                        <button
                          onClick={async () => {
                            if (editedMaxParticipants !== (league.max_participants || 0) && editedMaxParticipants >= league.current_participants) {
                              await handleUpdateMaxParticipants(editedMaxParticipants);
                            }
                          }}
                          disabled={isUpdatingMaxParticipants || editedMaxParticipants === (league.max_participants || 0) || editedMaxParticipants < league.current_participants}
                          className="text-green-400 hover:text-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Save"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingMaxParticipants(false);
                            setEditedMaxParticipants(league.max_participants || 0);
                          }}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          title="Cancel"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-white font-semibold">{league.current_participants}/{league.max_participants || 'N/A'}</span>
                        {isManager && (
                          <button
                            onClick={() => {
                              setEditedMaxParticipants(league.max_participants || 0);
                              setIsEditingMaxParticipants(true);
                            }}
                            className="text-blue-300 hover:text-blue-200 transition-colors ml-2"
                            title="Edit max participants"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-blue-200">Wrong Picks Allowed:</span>
                  <span className="text-white font-semibold">{leagueSettings.wrong_picks_allowed}</span>
                </div>
                {Array.isArray(participants) && participants.find(p => p.is_manager) && (
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-blue-200">League Manager:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-medium">
                        {Array.isArray(participants) && participants.find(p => p.is_manager)?.display_name}
                      </span>
                      <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-medium rounded-full border border-yellow-500/30">
                        Manager
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Share Information */}
              {(league.join_code || league.passcode) && (
                <div className="mt-6 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <h4 className="text-white font-semibold mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    Share League
                  </h4>
                  <p className="text-blue-200 text-sm mb-3">
                    Share this link with friends. They'll be able to join after logging in or signing up.
                  </p>
                  {(() => {
                    const shareUrl = `${window.location.origin}/join-league?code=${league.join_code || ''}&passcode=${league.passcode || ''}&type=eliminator`;
                    return (
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            readOnly
                            value={shareUrl}
                            className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm font-mono"
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(shareUrl);
                              alert('Link copied to clipboard!');
                            }}
                            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors flex items-center space-x-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Copy Link</span>
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {league.join_code && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                              Join Code: {league.join_code}
                            </span>
                          )}
                          {league.passcode && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                              Passcode: {league.passcode}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Participants */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <span className="mr-3">👥</span>
                Participants ({Array.isArray(participants) ? participants.length : 0})
              </h2>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {Array.isArray(participants) && participants.map((participant, index) => (
                  <div key={participant.user_id} className="flex items-center justify-between bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-500/80 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-white font-medium">{participant.display_name}</p>
                        <p className="text-blue-200 text-sm">{new Date(participant.joined_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    {participant.is_manager && (
                      <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-medium rounded-full border border-yellow-500/30">
                        Manager
                      </span>
                    )}
                    <div className="text-right">
                      <div className={`font-medium ${
                        participant.elimination_status === 'eliminated' ? 'text-red-300' : 'text-green-300'
                      }`}>
                        {participant.elimination_status === 'eliminated' ? '❌ Eliminated' : '✅ Active'}
                      </div>
                      <div className="text-sm text-blue-200">
                        {participant.wrong_picks_count} wrong picks
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Settings Section (Collapsible) - Full width below grid */}
          {showSettings && (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg mt-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <span className="mr-3">⚙️</span>
                  League Settings
                </h2>
                {leagueHasStarted && (
                  <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-yellow-200 font-medium">Settings Locked - League has started</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-6">
                {/* Wrong Picks Allowed Setting */}
                <div className="border-b border-white/10 pb-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h4 className="text-white font-medium">Wrong Picks Allowed</h4>
                      <p className="text-blue-200 text-sm">Number of incorrect predictions before elimination</p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-white">{leagueSettings.wrong_picks_allowed}</span>
                      <div className="text-blue-200 text-sm">picks</div>
                    </div>
                  </div>
                  
                  {isManager && !leagueHasStarted && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 space-y-3">
                      <p className="text-blue-200 text-sm">
                        <strong>Note:</strong> This setting cannot be changed once the league has started.
                      </p>
                      <div className="flex flex-col md:flex-row md:items-end gap-3">
                        <div className="flex-1">
                          <label className="block text-sm text-blue-100 mb-1">Wrong picks allowed</label>
                          <input
                            type="number"
                            min={1}
                            value={leagueSettings.wrong_picks_allowed}
                            onChange={(e) => setLeagueSettings(prev => ({ ...prev, wrong_picks_allowed: e.target.value }))}
                            className="w-full px-3 py-2 rounded-md bg-white/10 border border-blue-500/30 text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                        </div>
                        <button
                          onClick={handleSaveEliminatorSettings}
                          disabled={savingSettings}
                          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {savingSettings ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* League Management Actions */}
                {isManager && (
                  <div className="space-y-4">
                    <h4 className="text-white font-medium">League Management</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this league? This action cannot be undone.')) {
                            handleLeagueDeleted();
                          }
                        }}
                        className="bg-red-500/20 text-red-300 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-all duration-200 border border-red-500/30"
                      >
                        Delete League
                      </button>
                      
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to leave this league?')) {
                            handleLeaveLeague();
                          }
                        }}
                        className="bg-yellow-500/20 text-yellow-300 px-4 py-2 rounded-lg hover:bg-yellow-500/30 transition-all duration-200 border border-yellow-500/30"
                      >
                        Leave League
                      </button>
                    </div>
                  </div>
                )}

                {/* Non-manager actions */}
                {!isManager && (
                  <div className="space-y-4">
                    <h4 className="text-white font-medium">League Actions</h4>
                    
                    <button
                      onClick={() => {
                        if (window.confirm('Are you sure you want to leave this league?')) {
                          handleLeaveLeague();
                        }
                      }}
                      className="bg-yellow-500/20 text-yellow-300 px-4 py-2 rounded-lg hover:bg-yellow-500/30 transition-all duration-200 border border-yellow-500/30"
                    >
                      Leave League
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          </>
        )}

        {/* My Picks Tab */}
        {activeTab === 'mypicks' && (
          <div className="space-y-6">
            <div className="flex flex-row justify-between items-center gap-3">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center">
                <span className="mr-2 sm:mr-3">🎯</span>
                <span className="text-lg sm:text-xl md:text-2xl">My Predictions</span>
              </h2>
              {!eliminationStatus?.is_eliminated && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('dropdown')}
                    className={`px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 ${
                      viewMode === 'dropdown' 
                        ? 'bg-accent text-primary' 
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    Single Match
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 ${
                      viewMode === 'list' 
                        ? 'bg-accent text-primary' 
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    All Matches
                  </button>
                </div>
              )}
            </div>

            {/* Elimination Status Banner */}
            {eliminationStatus?.is_eliminated && (
              <div className="bg-red-500/20 border-2 border-red-500/50 rounded-xl p-6 shadow-lg">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-red-300 mb-2">You Have Been Eliminated</h3>
                    <p className="text-red-200 text-base">
                      You have made <span className="font-bold text-red-300">{eliminationStatus.wrong_picks}/{eliminationStatus.wrong_picks_allowed}</span> incorrect picks and are no longer able to make predictions in this league.
                    </p>
                    <p className="text-red-200 text-sm mt-2">
                      You can still view your past predictions and match results below.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications */}
            {notification && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800">{notification}</p>
              </div>
            )}
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {/* View Mode Content */}
            <>
              {/* Prediction Summary */}
              {Array.isArray(predictions) && predictions.length > 0 && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 shadow-lg mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center text-sm sm:text-base md:text-lg">
                    <span className="mr-2">📊</span>
                    <span className="text-xs sm:text-sm md:text-base">Your Prediction Summary</span>
                  </h3>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div className="text-center">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold text-green-300">
                        {predictions.filter(p => p.is_correct).length}
                      </div>
                      <div className="text-blue-200 text-xs sm:text-sm">Correct</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold text-red-300">
                        {eliminationStatus && eliminationStatus.wrong_picks !== undefined && eliminationStatus.wrong_picks_allowed !== undefined
                          ? `${eliminationStatus.wrong_picks}/${eliminationStatus.wrong_picks_allowed}`
                          : `${predictions.filter(p => p.is_correct === false).length}/${parseInt(leagueSettings?.wrong_picks_allowed || '3')}`}
                      </div>
                      <div className="text-blue-200 text-xs sm:text-sm">Wrong</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Dropdown View */}
              {viewMode === 'dropdown' && matches.length > 0 && !eliminationStatus?.is_eliminated && (
                <div className="space-y-6">
                  {/* Match Selection Dropdown */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-primary">Select Match</h3>
                      {selectedMatchId && getNextAvailableMatch()?.id === selectedMatchId && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Next Match
                        </span>
                      )}
                    </div>
                    <select 
                      value={selectedMatchId || ''}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                      onChange={(e) => setSelectedMatchId(e.target.value || null)}
                      disabled={eliminationStatus?.is_eliminated === true}
                    >
                      <option value="">Choose a match...</option>
                                      {Array.isArray(matches) && matches.map((match) => {
                  const matchStatus = getMatchStatusLocal(match);
                  const isLocked = isMatchLockedLocal(match);
                  const userPrediction = getPredictionForMatch(match.id);
                  const isCorrect = userPrediction?.is_correct;
                        
                        return (
                          <option key={match.id} value={match.id}>
                            {match.name} - {new Date(match.match_date).toLocaleDateString()}
                            {matchStatus === 'Completed' ? ' (Completed)' : 
                             matchStatus === 'Abandoned' ? ' (Abandoned)' :
                             matchStatus === 'Started' ? ' (Started)' : 
                             matchStatus === 'Live' ? ' (Live)' : ' (Upcoming)'}
                            {userPrediction && matchStatus === 'Completed' && (
                              isCorrect ? ' ✅' : ' ❌'
                            )}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Selected Match Prediction */}
                  {selectedMatchId && (() => {
                    const match = matches.find(m => m.id === selectedMatchId);
                    const prediction = getPredictionForMatch(selectedMatchId);
                    const isCompleted = Boolean(match?.winner && match.winner.trim() !== '');
                    const isCorrect = prediction && prediction.is_correct;
                    
                    if (!match) return null;
                    
                    return (
                      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg sm:text-xl font-bold text-primary mb-1 flex items-center gap-2">
                              {match.name}
                              {getMatchStatusLocal(match) === 'Completed' && (
                                <span className={`text-lg sm:text-xl ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                                  {isCorrect ? '✓' : '✗'}
                                </span>
                              )}
                            </h3>
                            <div className="text-gray-600 text-xs sm:text-sm flex flex-wrap items-center gap-2">
                              <span>📅 {new Date(match.match_date).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-6">
                          <h4 className="text-primary font-semibold text-center text-sm sm:text-base">
                            {getMatchStatusLocal(match) === 'Completed' ? 'Your Prediction & Result:' : 
                             getMatchStatusLocal(match) === 'Started' ? 'Your Prediction (Awaiting Result):' : 
                             'Select Your Pick:'}
                          </h4>
                          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                            <button
                              onClick={() => getMatchStatusLocal(match) === 'Upcoming' && !eliminationStatus?.is_eliminated && handleBatchPrediction(match.id, match.team1_name)}
                              disabled={getMatchStatusLocal(match) !== 'Upcoming' || eliminationStatus?.is_eliminated === true}
                              className={`flex items-center justify-center space-x-2 sm:space-x-3 py-2 sm:py-3 md:py-4 px-3 sm:px-4 md:px-6 rounded-lg border transition-all duration-300 relative ${
                                getMatchStatusLocal(match) === 'Completed'
                                  ? prediction?.predicted_winner === match.team1_name
                                    ? isCorrect
                                      ? 'border-green-500 bg-green-100 shadow-lg'
                                      : 'border-red-500 bg-red-100 shadow-lg'
                                    : match.winner === match.team1_name
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-300 bg-gray-50'
                                  : getMatchStatusLocal(match) === 'Started'
                                  ? prediction?.predicted_winner === match.team1_name
                                    ? 'border-orange-500 bg-orange-100 shadow-lg'
                                    : 'border-gray-300 bg-gray-50'
                                  : getCurrentPrediction(match.id) === match.team1_name
                                  ? 'border-yellow-500 bg-yellow-200 shadow-lg transform scale-105 hover:bg-gray-50'
                                  : 'border-gray-300 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <span className={`text-sm sm:text-base md:text-lg font-semibold ${
                                getMatchStatusLocal(match) === 'Completed'
                                  ? prediction?.predicted_winner === match.team1_name
                                    ? isCorrect
                                      ? 'text-green-800'
                                      : 'text-red-800'
                                    : match.winner === match.team1_name
                                    ? 'text-green-700'
                                    : 'text-gray-600'
                                  : getMatchStatusLocal(match) === 'Started'
                                  ? prediction?.predicted_winner === match.team1_name
                                    ? 'text-orange-800'
                                    : 'text-gray-600'
                                  : getCurrentPrediction(match.id) === match.team1_name 
                                  ? 'text-yellow-800'
                                  : 'text-primary'
                              }`}>{match.team1_name}</span>
                              {getMatchStatusLocal(match) === 'Completed' && match.winner === match.team1_name && (
                                <span className="text-green-600 font-bold text-sm sm:text-base">🏆</span>
                              )}
                              {getMatchStatusLocal(match) === 'Upcoming' && (getSavedPrediction(match.id) === match.team1_name || getCurrentPrediction(match.id) === match.team1_name) && (
                                <span className="text-green-600">
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                              {getMatchStatusLocal(match) === 'Started' && prediction?.predicted_winner === match.team1_name && (
                                <span className="text-orange-600 text-sm sm:text-base">⏳</span>
                              )}
                            </button>
                            <button
                              onClick={() => getMatchStatusLocal(match) === 'Upcoming' && !eliminationStatus?.is_eliminated && handleBatchPrediction(match.id, match.team2_name)}
                              disabled={getMatchStatusLocal(match) !== 'Upcoming' || eliminationStatus?.is_eliminated === true}
                              className={`flex items-center justify-center space-x-2 sm:space-x-3 py-2 sm:py-3 md:py-4 px-3 sm:px-4 md:px-6 rounded-lg border transition-all duration-300 relative ${
                                getMatchStatusLocal(match) === 'Completed'
                                  ? prediction?.predicted_winner === match.team2_name
                                    ? isCorrect
                                      ? 'border-green-500 bg-green-100 shadow-lg'
                                      : 'border-red-500 bg-red-100 shadow-lg'
                                    : match.winner === match.team2_name
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-300 bg-gray-50'
                                  : getMatchStatusLocal(match) === 'Started'
                                  ? prediction?.predicted_winner === match.team2_name
                                    ? 'border-orange-500 bg-orange-100 shadow-lg'
                                    : 'border-gray-300 bg-gray-50'
                                  : getCurrentPrediction(match.id) === match.team2_name
                                  ? 'border-yellow-500 bg-yellow-200 shadow-lg transform scale-105 hover:bg-gray-50'
                                  : 'border-gray-300 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <span className={`text-sm sm:text-base md:text-lg font-semibold ${
                                getMatchStatusLocal(match) === 'Completed'
                                  ? prediction?.predicted_winner === match.team2_name
                                    ? isCorrect
                                      ? 'text-green-800'
                                      : 'text-red-800'
                                    : match.winner === match.team2_name
                                    ? 'text-green-700'
                                    : 'text-gray-600'
                                  : getMatchStatusLocal(match) === 'Started'
                                  ? prediction?.predicted_winner === match.team2_name
                                    ? 'text-orange-800'
                                    : 'text-gray-600'
                                  : getCurrentPrediction(match.id) === match.team2_name 
                                  ? 'text-yellow-800'
                                  : 'text-primary'
                              }`}>{match.team2_name}</span>
                              {getMatchStatusLocal(match) === 'Completed' && match.winner === match.team2_name && (
                                <span className="text-green-600 font-bold text-sm sm:text-base">🏆</span>
                              )}
                              {getMatchStatusLocal(match) === 'Upcoming' && (getSavedPrediction(match.id) === match.team2_name || getCurrentPrediction(match.id) === match.team2_name) && (
                                <span className="text-green-600">
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                              {getMatchStatusLocal(match) === 'Started' && prediction?.predicted_winner === match.team2_name && (
                                <span className="text-orange-600 text-sm sm:text-base">⏳</span>
                              )}
                            </button>
                          </div>
                          
                          {/* Save Prediction Button - Always show, but disable when no prediction */}
                          {getMatchStatusLocal(match) === 'Upcoming' && (
                            <div className="text-center">
                              <button
                                onClick={async () => {
                                  try {
                                    const leagueId = parseInt(id!);
                                    const currentPrediction = getCurrentPrediction(match.id);
                                    
                                    if (!currentPrediction) {
                                      setError('Please select a team first');
                                      return;
                                    }
                                    
                                    console.log('🔍 Saving dropdown prediction for match:', match.id, {
                                      predictedWinner: currentPrediction
                                    });
                                    
                                    await apiService.makeEliminatorPrediction(leagueId, match.id, currentPrediction);
                                    
                                    // Reload predictions
                                    const predictionsData = await apiService.getEliminatorPredictions(leagueId);
                                    setPredictions(predictionsData.predictions);
                                    
                                    // Update batch state to reflect saved data
                                    const updatedBatchPredictions = { ...batchPredictions };
                                    if (currentPrediction) {
                                      updatedBatchPredictions[match.id] = currentPrediction;
                                    }
                                    setBatchPredictions(updatedBatchPredictions);
                                    
                                    console.log('🔍 Dropdown prediction saved successfully');
                                    setNotification('Prediction saved successfully!');
                                    setTimeout(() => setNotification(null), 3000);
                                  } catch (err: any) {
                                    console.error('🔍 Error saving dropdown prediction:', err);
                                    setError(err.message || 'Failed to save prediction');
                                  }
                                }}
                                disabled={!getCurrentPrediction(match.id)}
                                className={`py-2.5 sm:py-3 px-6 sm:px-8 rounded-lg text-base sm:text-lg font-medium transition-all duration-200 ${
                                  getCurrentPrediction(match.id)
                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                              >
                                Save
                              </button>
                            </div>
                          )}
                          
                          {/* Match Result for Completed Matches */}
                          {isCompleted && prediction && (
                            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                              <h5 className="text-gray-800 font-semibold mb-2 sm:mb-3 flex items-center text-sm sm:text-base">
                                <span className="mr-1 sm:mr-2">📊</span>
                                <span className="text-xs sm:text-sm md:text-base">Match Results Summary</span>
                              </h5>
                              <div className="overflow-x-auto -mx-2 sm:mx-0">
                                <table className="w-full text-xs sm:text-sm min-w-[300px]">
                                  <tbody>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 px-2 sm:px-3">
                                        <span className="text-gray-600 text-xs sm:text-sm">Your Prediction:</span>
                                      </td>
                                      <td className="py-2 px-2 sm:px-3">
                                        <span className="text-gray-800 font-medium text-xs sm:text-sm flex items-center gap-2">
                                          {prediction.predicted_winner}
                                          {isCorrect ? (
                                            <span className="text-green-600 text-base">✓</span>
                                          ) : (
                                            <span className="text-red-600 text-base">✗</span>
                                          )}
                                        </span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="py-2 px-2 sm:px-3">
                                        <span className="text-gray-600 text-xs sm:text-sm">Actual Winner:</span>
                                      </td>
                                      <td className="py-2 px-2 sm:px-3">
                                        <span className="text-gray-800 font-medium text-xs sm:text-sm">{match.winner}</span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                          
                          {/* Match Status for Started but Not Completed Matches */}
                          {!isCompleted && getMatchStatusLocal(match) === 'Started' && prediction && (
                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                              <h5 className="text-blue-800 font-semibold mb-3 flex items-center">
                                <span className="mr-2">⏳</span>
                                Match Status
                              </h5>
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-blue-600">Your Prediction:</span>
                                  <span className="text-blue-800 font-medium">{prediction.predicted_winner}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-blue-600">Match Status:</span>
                                  <span className="text-blue-800 font-medium">Started - Awaiting Result</span>
                                </div>
                                <div className="text-blue-600 text-sm">
                                  Result will be available once the admin sets the match winner.
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* No Upcoming Matches Message */}
                  {Array.isArray(matches) && matches.filter(m => getMatchStatusLocal(m) === 'Upcoming').length === 0 && (
                    <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-6 text-center">
                      <div className="text-yellow-200">
                        <svg className="w-12 h-12 mx-auto mb-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="text-lg font-semibold mb-2">No Upcoming Matches</h3>
                        <p className="text-yellow-300">All matches in this tournament have been started or completed.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* No Matches Message */}
              {viewMode === 'dropdown' && (!Array.isArray(matches) || matches.length === 0) && !eliminationStatus?.is_eliminated && (
                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-6 text-center">
                  <div className="text-yellow-200">
                    <svg className="w-12 h-12 mx-auto mb-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-lg font-semibold mb-2">No Matches Available</h3>
                    <p className="text-yellow-300">
                      {!Array.isArray(matches) 
                        ? 'Matches data is not available yet.' 
                        : 'No matches found for this tournament.'
                      }
                    </p>
                    <p className="text-yellow-300 text-sm mt-2">
                      Debug: matches = {JSON.stringify(matches)}
                    </p>
                  </div>
                </div>
              )}

              {/* Read-only view for eliminated users */}
              {eliminationStatus?.is_eliminated && matches.length > 0 && (
                <div className="space-y-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 shadow-lg">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                      <span className="mr-2">📋</span>
                      Your Past Predictions (Read-Only)
                    </h3>
                  </div>
                  
                  {Array.isArray(matches) && matches.map((match) => {
                    const userPrediction = getPredictionForMatch(match.id);
                    const matchStatus = getMatchStatusLocal(match);
                    const isCompleted = matchStatus === 'Completed';
                    const isCorrect = userPrediction?.is_correct;
                    
                    if (!userPrediction) return null;
                    
                    return (
                      <div key={match.id} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg sm:text-xl font-bold text-primary mb-1 flex items-center gap-2">
                              {match.name}
                              {isCompleted && (
                                <span className={`text-lg sm:text-xl ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                                  {isCorrect ? '✓' : '✗'}
                                </span>
                              )}
                            </h3>
                            <div className="text-gray-600 text-xs sm:text-sm flex flex-wrap items-center gap-2">
                              <span>📅 {new Date(match.match_date).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <div className={`flex items-center justify-center space-x-2 sm:space-x-3 py-2 sm:py-3 px-3 sm:px-4 rounded-lg border ${
                              userPrediction.predicted_winner === match.team1_name
                                ? isCompleted && isCorrect
                                  ? 'border-green-500 bg-green-100'
                                  : isCompleted && !isCorrect
                                  ? 'border-red-500 bg-red-100'
                                  : 'border-yellow-500 bg-yellow-100'
                                : 'border-gray-300 bg-gray-50'
                            }`}>
                              <span className={`text-sm sm:text-base font-semibold ${
                                userPrediction.predicted_winner === match.team1_name
                                  ? isCompleted && isCorrect
                                    ? 'text-green-800'
                                    : isCompleted && !isCorrect
                                    ? 'text-red-800'
                                    : 'text-yellow-800'
                                  : 'text-gray-600'
                              }`}>{match.team1_name}</span>
                              {userPrediction.predicted_winner === match.team1_name && (
                                <span className="text-green-600">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </div>
                            <div className={`flex items-center justify-center space-x-2 sm:space-x-3 py-2 sm:py-3 px-3 sm:px-4 rounded-lg border ${
                              userPrediction.predicted_winner === match.team2_name
                                ? isCompleted && isCorrect
                                  ? 'border-green-500 bg-green-100'
                                  : isCompleted && !isCorrect
                                  ? 'border-red-500 bg-red-100'
                                  : 'border-yellow-500 bg-yellow-100'
                                : 'border-gray-300 bg-gray-50'
                            }`}>
                              <span className={`text-sm sm:text-base font-semibold ${
                                userPrediction.predicted_winner === match.team2_name
                                  ? isCompleted && isCorrect
                                    ? 'text-green-800'
                                    : isCompleted && !isCorrect
                                    ? 'text-red-800'
                                    : 'text-yellow-800'
                                  : 'text-gray-600'
                              }`}>{match.team2_name}</span>
                              {userPrediction.predicted_winner === match.team2_name && (
                                <span className="text-green-600">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {isCompleted && (
                            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                              <div className="overflow-x-auto -mx-2 sm:mx-0">
                                <table className="w-full text-xs sm:text-sm min-w-[300px]">
                                  <tbody>
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 px-2 sm:px-3">
                                        <span className="text-gray-600 text-xs sm:text-sm">Your Prediction:</span>
                                      </td>
                                      <td className="py-2 px-2 sm:px-3">
                                        <span className="text-gray-800 font-medium text-xs sm:text-sm flex items-center gap-2">
                                          {userPrediction.predicted_winner}
                                          {isCorrect ? (
                                            <span className="text-green-600 text-base">✓</span>
                                          ) : (
                                            <span className="text-red-600 text-base">✗</span>
                                          )}
                                        </span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="py-2 px-2 sm:px-3">
                                        <span className="text-gray-600 text-xs sm:text-sm">Actual Winner:</span>
                                      </td>
                                      <td className="py-2 px-2 sm:px-3">
                                        <span className="text-gray-800 font-medium text-xs sm:text-sm">{match.winner || 'TBD'}</span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* List View */}
              {viewMode === 'list' && matches.length > 0 && !eliminationStatus?.is_eliminated && (
                <div className="space-y-6">
                  {/* Save Button */}
                  <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-center mb-4 sm:mb-6">
                      <button
                        onClick={handleSaveBatchPredictions}
                        disabled={Object.keys(batchPredictions).length === 0 || savingPredictions || eliminationStatus?.is_eliminated === true}
                        className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 ${
                          Object.keys(batchPredictions).length > 0 && !savingPredictions && !eliminationStatus?.is_eliminated
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {savingPredictions ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>

                  {Array.isArray(matches) && matches.map((match) => {
                    const matchStatus = getMatchStatusLocal(match);
                    const isLocked = isMatchLockedLocal(match);
                    const isCompleted = matchStatus === 'Completed';
                    const userPrediction = getPredictionForMatch(match.id);
                    const isCorrect = userPrediction?.is_correct;
                    
                    return (
                      <div key={match.id} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg sm:text-xl font-bold text-primary mb-1 flex items-center gap-2">
                              {match.name}
                              {isCompleted && (
                                <span className={`text-lg sm:text-xl ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                                  {isCorrect ? '✓' : '✗'}
                                </span>
                              )}
                            </h3>
                            <div className="text-gray-600 text-xs sm:text-sm flex flex-wrap items-center gap-2">
                              <span>📅 {new Date(match.match_date).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>

                        {/* Team Selection Display */}
                        <div className="space-y-6 mb-6">
                          <h4 className="text-primary font-semibold text-center text-sm sm:text-base">
                            {isCompleted ? 'Match Result:' : 'Teams:'}
                          </h4>
                          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                            <button
                              onClick={() => !isLocked && !eliminationStatus?.is_eliminated && handleBatchPrediction(match.id, match.team1_name)}
                              disabled={isLocked || eliminationStatus?.is_eliminated === true}
                              className={`flex items-center justify-center space-x-2 sm:space-x-3 py-2 sm:py-3 md:py-4 px-3 sm:px-4 md:px-6 rounded-lg border transition-all duration-300 ${
                                isCompleted
                                  ? match.winner === match.team1_name
                                    ? 'border-green-500 bg-green-100 shadow-lg'
                                    : userPrediction?.predicted_winner === match.team1_name && !isCorrect
                                    ? 'border-red-500 bg-red-100 shadow-lg'
                                    : 'border-gray-300 bg-gray-50'
                                  : getCurrentPrediction(match.id) === match.team1_name
                                  ? 'border-yellow-500 bg-yellow-200 shadow-lg transform scale-105 hover:bg-gray-50'
                                  : 'border-gray-300 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <span className={`text-sm sm:text-base md:text-lg font-semibold ${
                                isCompleted
                                  ? match.winner === match.team1_name
                                    ? 'text-green-800'
                                    : userPrediction?.predicted_winner === match.team1_name && !isCorrect
                                    ? 'text-red-800'
                                    : 'text-gray-600'
                                  : getCurrentPrediction(match.id) === match.team1_name
                                  ? 'text-yellow-800'
                                  : 'text-primary'
                              }`}>{match.team1_name}</span>
                              {isCompleted && match.winner === match.team1_name && (
                                <span className="text-green-600 font-bold text-sm sm:text-base">🏆</span>
                              )}
                              {!isLocked && (getSavedPrediction(match.id) === match.team1_name || getCurrentPrediction(match.id) === match.team1_name) && (
                                <span className="text-green-600">
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </button>
                            <button
                              onClick={() => !isLocked && !eliminationStatus?.is_eliminated && handleBatchPrediction(match.id, match.team2_name)}
                              disabled={isLocked || eliminationStatus?.is_eliminated === true}
                              className={`flex items-center justify-center space-x-2 sm:space-x-3 py-2 sm:py-3 md:py-4 px-3 sm:px-4 md:px-6 rounded-lg border transition-all duration-300 ${
                                isCompleted
                                  ? match.winner === match.team2_name
                                    ? 'border-green-500 bg-green-100 shadow-lg'
                                    : userPrediction?.predicted_winner === match.team2_name && !isCorrect
                                    ? 'border-red-500 bg-red-100 shadow-lg'
                                    : 'border-gray-300 bg-gray-50'
                                  : getCurrentPrediction(match.id) === match.team2_name
                                  ? 'border-yellow-500 bg-yellow-200 shadow-lg transform scale-105 hover:bg-gray-50'
                                  : 'border-gray-300 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <span className={`text-sm sm:text-base md:text-lg font-semibold ${
                                isCompleted
                                  ? match.winner === match.team2_name
                                    ? 'text-green-800'
                                    : userPrediction?.predicted_winner === match.team2_name && !isCorrect
                                    ? 'text-red-800'
                                    : 'text-gray-600'
                                  : getCurrentPrediction(match.id) === match.team2_name
                                  ? 'text-yellow-800'
                                  : 'text-primary'
                              }`}>{match.team2_name}</span>
                              {isCompleted && match.winner === match.team2_name && (
                                <span className="text-green-600 font-bold text-sm sm:text-base">🏆</span>
                              )}
                              {!isLocked && (getSavedPrediction(match.id) === match.team2_name || getCurrentPrediction(match.id) === match.team2_name) && (
                                <span className="text-green-600">
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </button>
                          </div>
                        </div>

                        {isLocked && userPrediction && (
                          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                            <h5 className="text-gray-800 font-semibold mb-2 sm:mb-3 flex items-center text-sm sm:text-base">
                              <span className="mr-1 sm:mr-2">📊</span>
                              <span className="text-xs sm:text-sm md:text-base">Match Results Summary</span>
                            </h5>
                            <div className="overflow-x-auto -mx-2 sm:mx-0">
                              <table className="w-full text-xs sm:text-sm min-w-[300px]">
                                <tbody>
                                  <tr className="border-b border-gray-100">
                                    <td className="py-2 px-2 sm:px-3">
                                      <span className="text-gray-600 text-xs sm:text-sm">Your Prediction:</span>
                                    </td>
                                    <td className="py-2 px-2 sm:px-3">
                                      <span className="text-gray-800 font-medium text-xs sm:text-sm flex items-center gap-2">
                                        {userPrediction.predicted_winner}
                                        {isCorrect ? (
                                          <span className="text-green-600 text-base">✓</span>
                                        ) : (
                                          <span className="text-red-600 text-base">✗</span>
                                        )}
                                      </span>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="py-2 px-2 sm:px-3">
                                      <span className="text-gray-600 text-xs sm:text-sm">Actual Winner:</span>
                                    </td>
                                    <td className="py-2 px-2 sm:px-3">
                                      <span className="text-gray-800 font-medium text-xs sm:text-sm">{match.winner || 'TBD'}</span>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white flex items-center mb-6">
              <span className="mr-3">📝</span>
              Activity
            </h2>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
              <h3 className="text-lg font-semibold text-white mb-6">Recent Activity</h3>
              <div className="space-y-4">
                {Array.isArray(participants) && participants.length > 0 ? (
                  participants.map((participant) => (
                    <div key={participant.user_id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-500/80 rounded-full flex items-center justify-center text-white font-semibold">
                          {participant.display_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-white">{participant.display_name}</div>
                          <div className="text-sm text-blue-200">
                            Joined {new Date(participant.joined_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-medium ${
                          participant.elimination_status === 'eliminated' ? 'text-red-300' : 'text-green-300'
                        }`}>
                          {participant.elimination_status === 'eliminated' ? '❌ Eliminated' : '✅ Active'}
                        </div>
                        <div className="text-sm text-blue-200">
                          {participant.wrong_picks_count} wrong picks
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-blue-200">No recent activity yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Survivors Tab */}
        {activeTab === 'survivors' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white flex items-center mb-6">
              <span className="mr-3">🏆</span>
              Survivors Grid
            </h2>
            

            

            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg">
              <div className="overflow-x-auto">
                {Array.isArray(matches) && Array.isArray(allPredictions) && Array.isArray(survivors) ? (
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-white/20">
                        <th className="text-left py-3 px-4 font-semibold text-white sticky left-0 bg-white/10 backdrop-blur-sm z-10">
                          Player
                        </th>
                        {matches.map((match, index) => (
                          <th key={match.id} className="text-center py-3 px-2 font-semibold text-white min-w-[120px]">
                            <div className="text-xs">
                              <div className="font-bold">Match {index + 1}</div>
                              <div className="text-blue-200 truncate">{getShortMatchName(match)}</div>
                              <div className="text-gray-300 text-xs">
                                {new Date(match.match_date).toLocaleDateString()}
                              </div>
                            </div>
                          </th>
                        ))}
                        <th className="text-center py-3 px-4 font-semibold text-white min-w-[100px]">
                          Wrong Picks Left
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {survivors.map((survivor) => (
                        <tr key={survivor.user_id} className="border-b border-white/10 hover:bg-white/5">
                          {/* Player Name Column */}
                          <td className="py-3 px-4 sticky left-0 bg-white/10 backdrop-blur-sm z-10">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-500/80 rounded-full flex items-center justify-center text-white font-semibold">
                                {survivor.display_name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-medium text-white">{survivor.display_name}</div>
                                {survivor.is_manager && (
                                  <div className="text-xs text-blue-300 font-medium">Manager</div>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          {/* Match Prediction Columns */}
                          {matches.map((match) => {
                            // If player is eliminated, show X for all matches
                            if (survivor.status === 'eliminated') {
                              return (
                                <td key={match.id} className="text-center py-3 px-2">
                                  <div className="text-red-400 text-xs font-bold">X</div>
                                </td>
                              );
                            }
                            
                            const userPrediction = allPredictions.find(p => 
                              p.user_id === survivor.user_id && p.match_id === match.id
                            );
                            const matchStatus = getMatchStatusLocal(match);
                            const isMatchStarted = matchStatus === 'Started' || matchStatus === 'Live' || matchStatus === 'Completed';
                            const isMatchCompleted = matchStatus === 'Completed';
                            const isCorrect = userPrediction?.is_correct;
                            const wrongPicksAllowed = parseInt(leagueSettings?.wrong_picks_allowed || '0');
                            
                            return (
                              <td key={match.id} className="text-center py-3 px-2">
                                {!isMatchStarted ? (
                                  // Match hasn't started yet - show dash
                                  <div className="text-gray-400 text-xs font-bold">-</div>
                                ) : !userPrediction ? (
                                  // Match started but no prediction made - show X
                                  <div className="text-red-400 text-xs font-bold">X</div>
                                ) : isMatchCompleted ? (
                                  // Match completed - show result with color
                                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                                    isCorrect 
                                      ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                                      : 'bg-red-500/20 text-red-300 border border-red-500/30'
                                  }`}>
                                    {getPredictedWinnerShortName(userPrediction.predicted_winner, match)}
                                    <div className="text-xs mt-1">
                                      {isCorrect ? '✅' : '❌'}
                                    </div>
                                  </div>
                                ) : (
                                  // Match started but not completed - show prediction (no result yet)
                                  <div className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                                    {getPredictedWinnerShortName(userPrediction.predicted_winner, match)}
                                    <div className="text-xs mt-1">⏳</div>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          
                          {/* Wrong Picks Left Column */}
                          <td className="text-center py-3 px-4">
                            {(() => {
                              const wrongPicksAllowed = parseInt(leagueSettings?.wrong_picks_allowed || '0');
                              const wrongPicksLeft = wrongPicksAllowed - survivor.wrong_predictions;
                              return (
                                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                                  survivor.status === 'eliminated' 
                                    ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
                                    : survivor.wrong_predictions >= wrongPicksAllowed
                                    ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                                    : 'bg-green-500/20 text-green-300 border border-green-500/30'
                                }`}>
                                  {survivor.status === 'eliminated' ? (
                                    <span>❌ Eliminated</span>
                                  ) : (
                                    <span>{wrongPicksLeft} left</span>
                                  )}
                                  {/* Debug info */}
                                  <div className="text-xs mt-1 text-gray-400">
                                    ({survivor.wrong_predictions}/{wrongPicksAllowed})
                                  </div>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-blue-200">Loading survivors data...</p>
                  </div>
                )}
              </div>
              
              {/* Legend */}
              <div className="mt-6 pt-4 border-t border-white/20">
                <h4 className="text-white font-semibold mb-3">Legend:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-green-500/20 border border-green-500/30 rounded"></div>
                    <span className="text-green-300">Correct Prediction</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-red-500/20 border border-red-500/30 rounded"></div>
                    <span className="text-red-300">Wrong Prediction</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-blue-500/20 border border-blue-500/30 rounded"></div>
                    <span className="text-blue-300">Started - Awaiting Result</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-gray-500/20 border border-gray-500/30 rounded"></div>
                    <span className="text-gray-300">Not Started (-)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-red-500/20 border border-red-500/30 rounded"></div>
                    <span className="text-red-300">No Pick (X)</span>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-400">
                  <p><strong>Note:</strong> Predictions are only evaluated as correct/incorrect after the admin sets the match result.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EliminatorLeaguePage; 